import { Button, Input, Modal, Typography, Grid, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { ApplicationsFilter } from '../../../shared/VolunteerApplicationServiceTypes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { VALID_ABHYASI_ID_REGEX, Gender } from '../../../shared/Constants';
import { UndoOutlined } from '@ant-design/icons';
import FilterIcon from '../../../assets/images/filter-icon.svg';
import { useProposerLoad } from './useProposerLoad';
import useQueryParams from '../../../hooks/useQueryParams';
import { PROPOSER_LOAD_ZONES } from '../../../shared/Routes';

interface FilterProps {
	value: ApplicationsFilter;
	onFilterChange: (value: ApplicationsFilter) => void;
	disableBulkAssign: boolean;
}

const { Text } = Typography;
const { useBreakpoint } = Grid;
type GenderType = (typeof Gender)[keyof typeof Gender];

export const Filter = ({
	value: {
		searchByApplicantName,
		zoneNames,
		applicantSrcmId,
		searchByProposerName,
		searchByProposerGender,
	},
	onFilterChange,
	disableBulkAssign,
}: FilterProps) => {
	const { t, i18n } = useTranslation();
	const lengthyLanguages = ['ta', 'te', 'ka', 'ma'];
	const isLengthyLanguage = lengthyLanguages.includes(i18n.language);
	const screens = useBreakpoint();
	const { onBulkAssign } = useProposerLoad();
	const queryParams = useQueryParams();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;
	const [nameValue, setNameValue] = useState<string | undefined>(
		searchByApplicantName
	);
	const [proposerNameValue, setProposerNameValue] = useState<
		string | undefined
	>(searchByProposerName);
	const [zones, setZones] = useState<Array<string> | undefined>(zoneNames);
	const [applicantSrcmIdValue, setApplicantSrcmId] = useState<
		string | undefined
	>(applicantSrcmId);
	const [proposerSrcmId, setProposerSrcmId] = useState<string | undefined>();
	const [isFilterPopup, setIsFilterPopup] = useState<boolean>(false);
	const [isFilterTextApplied, setIsFilterTextApplied] =
		useState<boolean>(false);
	const [applicantSrcmIdError, setApplicantSrcmIdError] = useState('');
	const [proposerSrcmIdError, setProposerSrcmIdError] = useState('');
	const [genderValue, setGenderValue] = useState<string | undefined>(
		searchByProposerGender
	);

	const resetFilter = useCallback(() => {
		setGenderValue('NOT_PROVIDED');
		setNameValue('');
		setProposerNameValue('');
		setProposerSrcmId('');
		setProposerSrcmIdError('');
		setZones([]);
		setApplicantSrcmId('');
		setApplicantSrcmIdError('');
	}, []);

	useEffect(() => {
		resetFilter();
	}, [resetFilter]);

	const fireFilterChange = useCallback(() => {
		onFilterChange({
			applicantSrcmId: applicantSrcmIdValue,
			searchByApplicantName: nameValue,
			searchByProposerSrcmId: proposerSrcmId,
			zoneNames: zones,
			searchByProposerName: proposerNameValue,
			searchByProposerGender: genderValue,
		});
	}, [
		applicantSrcmIdValue,
		nameValue,
		onFilterChange,
		proposerSrcmId,
		zones,
		proposerNameValue,
		genderValue,
	]);

	const handleFilterButtonClick = useCallback(() => {
		fireFilterChange();
		setIsFilterPopup(false);
		setIsFilterTextApplied(true);
	}, [fireFilterChange]);

	const handleResetFilters = useCallback(() => {
		resetFilter();
		setZones([]);
		onFilterChange({});
		setIsFilterPopup(false);
		setIsFilterTextApplied(false);
	}, [onFilterChange, resetFilter]);

	const genderDropdownOptions = useMemo(() => {
		const genderLabels: Record<GenderType, string> = {
			[Gender.NOT_PROVIDED]: 'bothGenders',
			[Gender.MALE]: 'male',
			[Gender.FEMALE]: 'female',
		};

		const renderLabel = (labelKey: string) => (
			<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
				{t(labelKey)}
			</p>
		);

		return Object.entries(genderLabels).map(([value, labelKey]) => ({
			value,
			label: renderLabel(labelKey),
		}));
	}, [t]);

	const handleGenderChange = useCallback((value: string) => {
		setGenderValue(value);
	}, []);

	const handleBulkAssign = useCallback(() => {
		const zoneId = [Number(queryParams.get('zoneId'))];
		onBulkAssign([], zoneId, PROPOSER_LOAD_ZONES);
	}, [queryParams, onBulkAssign]);

	const onApplicantNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setNameValue(e.target.value);
		},
		[]
	);

	const onProposerNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setProposerNameValue(e.target.value);
		},
		[]
	);

	const onApplicantSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value.toUpperCase();
			setApplicantSrcmId(value);
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setApplicantSrcmIdError('invalidAbhyasiId');
			} else {
				setApplicantSrcmIdError('');
			}
		},
		[]
	);

	const onProposerSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value.toUpperCase();
			setProposerSrcmId(value);
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setProposerSrcmIdError('invalidAbhyasiId');
			} else {
				setProposerSrcmIdError('');
			}
		},
		[]
	);

	const handleFilterPopup = useCallback(() => {
		setIsFilterPopup(true);
	}, []);

	const onClose = useCallback(() => {
		setIsFilterPopup(false);
	}, []);

	return (
		<>
			<div className="flex flex-row items-baseline h-full min-h-0 gap-2 ml-8">
				<div className="flex justify-end w-1/6 sm:w-auto">
					<Button
						className="border border-primary-blue text-primary-blue font-semibold"
						variant="outlined"
						onClick={handleBulkAssign}
						disabled={disableBulkAssign}
					>
						{t('bulkAssign')}
					</Button>
				</div>
				<div className="flex justify-end w-1/6 sm:w-auto">
					<Button
						className="block sm:hidden"
						variant="outlined"
						onClick={handleFilterPopup}
					>
						<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
					</Button>
					<Button
						className={`hidden sm:block text-primary-blue font-sans font-bold text-sm border border-solid mt-0.5 ${
							isFilterTextApplied ? 'border-primary-blue' : 'border-gray-200'
						}`}
						variant="outlined"
						onClick={handleFilterPopup}
					>
						{isFilterTextApplied ? t('filterApplied') : t('filter')}
					</Button>

					{isFilterTextApplied ? (
						<Button
							className="hidden sm:block flex-1 h-9 w-20 text-red-500 font-sans font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					) : (
						''
					)}
				</div>
				<Modal
					open={isFilterPopup}
					onCancel={onClose}
					footer={null}
					centered
					className={
						isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
					}
					closable={true}
					maskClosable={true}
				>
					<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
						{t('advancedFilter')}
					</div>

					<div className="flex flex-col sm:flex-row flex-wrap gap-x-4">
						<div className="w-full sm:w-[calc(50%-0.5rem)]">
							<div className="mb-[16px] flex flex-col gap-2">
								<Text className="text-xs text-gray-650">
									{t('filterByApplicantName')}
								</Text>
								<Input
									className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
									value={nameValue}
									allowClear
									onChange={onApplicantNameChange}
								/>
							</div>
						</div>
						<div className="w-full sm:w-[calc(50%-0.5rem)]">
							<div className="mb-[16px] flex flex-col gap-2">
								<Text className="text-xs text-gray-650">
									{t('filterByApplicantSRCMId')}
								</Text>
								<Input
									className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
									value={applicantSrcmIdValue}
									allowClear
									onChange={onApplicantSRCMIdChange}
								/>
								{applicantSrcmIdError && (
									<Text className="text-xs text-red-500">
										{t(applicantSrcmIdError)}
									</Text>
								)}
							</div>
						</div>

						<div className="w-full sm:w-[calc(50%-0.5rem)]">
							<div className="mb-[16px] flex flex-col gap-2">
								<Text className="text-xs text-gray-650">
									{t('filterByProposerName')}
								</Text>
								<Input
									className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
									value={proposerNameValue}
									allowClear
									onChange={onProposerNameChange}
								/>
							</div>
						</div>

						<div className="w-full sm:w-[calc(50%-0.5rem)]">
							<div className="mb-[16px] flex flex-col gap-2">
								<Text className="text-xs text-gray-650">
									{t('filterByProposerSRCMId')}
								</Text>
								<Input
									className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
									value={proposerSrcmId}
									allowClear
									onChange={onProposerSRCMIdChange}
								/>
								{proposerSrcmIdError && (
									<Text className="text-xs text-red-500">
										{t(proposerSrcmIdError)}
									</Text>
								)}
							</div>
						</div>

						<div className="w-full sm:w-[calc(50%-0.5rem)]">
							<div className="mb-[16px] flex flex-col gap-2">
								<Text className="text-xs text-gray-650">
									{t('filterByProposerGender')}
								</Text>
								<Select
									className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
									value={genderValue ?? Gender.NOT_PROVIDED}
									options={genderDropdownOptions}
									onChange={handleGenderChange}
								/>
							</div>
						</div>
					</div>
					<div></div>
					<div className="flex justify-center">
						<Button
							className="block sm:hidden flex-1 h-9 text-primary-blue font-sans font-bold text-center"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}

								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					</div>
					<div
						className={`flex ${
							isStackVertical
								? 'flex-col items-center space-y-2'
								: 'justify-center gap-2'
						} mt-4 ant-modal-footer`}
					>
						<Button
							className="hidden sm:block flex-1 h-9 text-primary-blue font-sans font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
						<Button
							className={`flex-1 h-9 text-primary-blue font-sans font-bold ${
								isStackVertical ? 'p-2' : ''
							}`}
							onClick={onClose}
						>
							{t('closeFilter')}
						</Button>
						<Button
							type="primary"
							onClick={handleFilterButtonClick}
							className={`flex-1 h-9 font-sans font-bold ${
								isStackVertical ? 'p-2 mt-3' : ''
							}`}
							disabled={
								proposerSrcmIdError !== '' || applicantSrcmIdError !== ''
							}
						>
							{t('applyFilter')}
						</Button>
					</div>
				</Modal>
			</div>
		</>
	);
};
